import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Info } from 'lucide-react';
import { Link, Navigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { AuthHeroPanel } from '../components/AuthHeroPanel';
import {
  AuthErrorBanner,
  FieldError,
  clearErrorsOnChange,
  firstFieldError,
  shouldShowFieldError,
} from '../components/fieldError';
import { CreateAccountStep } from '../components/CreateAccountStep';
import { SsoButton } from '../components/SsoButton';
import { ActiveMfaChallenge } from '../components/MfaChallenge';
import { useAuthActions } from '../hooks/useAuthActions';
import { useAuthPrefillStore } from '../hooks/useAuthPrefillStore';
import { microsoftSsoEnabled } from '../microsoftSso';
import {
  PasswordChecklist,
  passwordMeetsPolicy,
} from '@/shared/components/PasswordPolicy';
import { useUserStore } from '@/entities/user/store';
import { config } from '@/shared/config/env';

/**
 * FINALIZED UI — reviewed against the Figma export and signed off.
 *
 * Do not redesign this screen. If a change to how it LOOKS seems obviously
 * right, read the comment next to the thing first: most of what reads as odd
 * here is a decision with its reason written beside it. See
 * features/Auth/README.md before editing, and change the shared component in
 * components/AuthShell.tsx or components/fieldError.tsx rather than this file,
 * so the flow does not drift apart again.
 *
 * This applies to AI agents as much as to people.
 */
/**
 * Sign-up page. 1:1 port of `SignIn.tsx`'s `isSignUp` branch from the
 * Figma export at
 * `~/Downloads/BIASafe AI - Quant Model Portfolio Designer (2)/`.
 * Keeps the existing useAuthActions wiring (orval mutations + email
 * verification step) underneath the Figma visual shell.
 */
const FONT_SERIF = 'var(--font-brand)';
const FONT_SANS = 'var(--font-brand)';

export function SignUpPage() {
  const { t } = useTranslation('auth');
  const actions = useAuthActions();
  const {
    signUp,
    beginGoogleSSO,
    beginMicrosoftSSO,
    mfaChallenge,
    ssoCreate,
    error,
    isLoading,
  } = actions;
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const [showPassword, setShowPassword] = useState(false);
  // UNTICKED by default. This reverses the 2026-08-31 decision to pre-tick it.
  //
  // A pre-ticked consent box is not affirmative consent under GDPR: CJEU
  // Planet49 (C-673/17) held exactly that, and Art. 4(11) requires a clear
  // affirmative act. Quebec's Law 25 and PIPEDA land in the same place. If
  // acceptance of the Terms or the Privacy Policy is ever disputed, a box the
  // user never touched is the weakest possible evidence that they agreed.
  //
  // The product owner approved changing this screen for the sign-up friction
  // fixes (the README still says FINALIZED; that approval is why this file is
  // open). The Sign Up button gates on this value, so the user's own tick is
  // what enables it. The disabled button says why through `aria-describedby`
  // (`terms_hint`), so a keyboard or screen-reader user is not left with a
  // dead button and no reason.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [passwordStarted, setPasswordStarted] = useState(false);
  // The address typed on the log-in page when a failed sign-in offered "create
  // an account". Read once; see useAuthPrefillStore.
  const [prefilledEmail] = useState(() => useAuthPrefillStore.getState().take().email);

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: prefilledEmail,
      password: '',
    },
    onSubmit: ({ value }) => {
      signUp({
        email: value.email,
        password: value.password,
        firstName: value.firstName,
        lastName: value.lastName,
      });
    },
  });

  // "Continue with Google" / "Continue with Microsoft" are the same flow as on
  // the log-in page: sign in with the provider, and when there is no account
  // offer "Create your account" (the `ssoCreate` step below). The page's own
  // terms box belongs to the email form only.

  // Same landing page as LoginPage — see the note there.
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  // Google or Microsoft recognised an existing account that has a second
  // factor. That is a log-in, so show the same challenge the log-in page does.
  if (mfaChallenge) return <ActiveMfaChallenge actions={actions} />;

  // No account for the provider address, and its sign-up is on.
  if (ssoCreate) return <CreateAccountStep actions={actions} />;

  // There is no verification screen here. The signup mutation navigates to
  // /verify-email the moment it has a userId, so this component is already
  // unmounting by the time `signupSuccess` is set — and without a userId the
  // old `signupSuccess && userId` guard was false anyway. The branch could not
  // render either way, which is why nobody ever reported the code form it held
  // asking for a code the email does not contain.

  return (
    <div
      /* `light`: see the note on AuthShell. SignUpPage holds its own copy of
         the shell rather than using it, so it needs the pin too. */
      className="light flex items-start relative w-full min-h-screen bg-white [-webkit-font-smoothing:antialiased] [-moz-osx-font-smoothing:grayscale]"
      data-name="SignUp"
    >
      <AuthHeroPanel />

      <div
        className="flex-1 bg-white relative h-screen overflow-y-auto overflow-x-hidden min-w-0"
        data-name="wrapper"
      >
        <div className="flex flex-col min-h-full">
          <div className="w-full flex justify-center pt-[48px] pb-[16px] sticky top-0 bg-white z-10 shrink-0">
            <div
              className="content-stretch flex gap-[10px] items-center justify-center leading-[0] relative shrink-0"
              data-name="logo"
            >
              <img
                src={`${config.app.basePath}biasafe-logo.png`}
                alt={t('signup.logo_alt')}
                className="h-[38px] w-auto"
              />
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-8 sm:py-10 md:py-12 lg:py-14 xl:py-16">
            <div className="w-full flex flex-col items-center">
              <div className="content-stretch flex flex-col gap-[18px] items-center relative shrink-0 w-full min-w-[144px] max-w-[256px] sm:max-w-[360px] md:max-w-[410px] lg:max-w-[460px] xl:max-w-[540px]">
                <div
                  className="content-stretch flex flex-col gap-[8px] items-center leading-[0] relative shrink-0 text-center w-full"
                  data-name="header-text"
                >
                  <div
                    className="flex flex-col font-medium justify-center relative shrink-0 text-[#121212] text-[48px] tracking-[-1.5px] w-full"
                    style={{ fontFamily: FONT_SERIF }}
                  >
                    <p className="leading-[48px] text-[42px]">
                      {t('signup.title')}
                    </p>
                  </div>
                  <div
                    className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#3e3e3e] text-[16px] text-nowrap"
                    style={{ fontFamily: FONT_SANS }}
                  >
                    <p className="leading-[24px] whitespace-pre">
                      <span className="not-italic text-[#3e3e3e]">
                        {t('signup.subtitle_prefix')}
                      </span>{' '}
                      <Link
                        to="/login"
                        className="not-italic text-[#121212] cursor-pointer hover:underline font-bold"
                      >
                        {t('signup.subtitle_link')}
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="w-full">
                  {error && (
                    <AuthErrorBanner className="mb-4">
                      {error}
                    </AuthErrorBanner>
                  )}

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      form.handleSubmit();
                    }}
                    className="content-stretch flex flex-col gap-[16px] items-center relative shrink-0 w-full"
                  >
                    {/* First + Last Name row. */}
                    <div className="flex gap-[12px] items-start relative shrink-0 w-full overflow-hidden">
                      <NameField
                        form={form}
                        name="firstName"
                        label={t('signup.first_name_label')}
                        placeholder={t('signup.first_name_placeholder')}
                        autoComplete="given-name"
                      />
                      <NameField
                        form={form}
                        name="lastName"
                        label={t('signup.last_name_label')}
                        placeholder={t('signup.last_name_placeholder')}
                        autoComplete="family-name"
                      />
                    </div>

                    {/* Email. */}
                    <EmailField form={form} />

                    {/* Password with strength UI. */}
                    <PasswordField
                      form={form}
                      label={t('signup.password_label')}
                      placeholder={t('signup.password_placeholder')}
                      autoComplete="new-password"
                      showPassword={showPassword}
                      onToggleShow={() => setShowPassword(!showPassword)}
                      started={passwordStarted}
                      onChangeStarted={setPasswordStarted}
                      showRequirements
                    />

                    {/* Accept Terms checkbox. */}
                    <div className="content-stretch flex items-center relative shrink-0 w-full">
                      <div className="content-stretch flex gap-[6px] items-center relative shrink-0">
                        <div className="box-border content-stretch flex gap-[11.25px] items-start justify-center p-[1.125px] relative shrink-0">
                          <input
                            id="accept-terms"
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                            // `accent-gray-400` filled the box with a mid grey
                            // and drew the tick over it in near-black — two
                            // muddy tones on top of each other, which is why it
                            // read as heavy rather than as ticked. `#121212`
                            // gives a crisp black box with a white tick, and
                            // matches the Sign Up button below it.
                            className="relative rounded-[calc(var(--corner-radius)*0.375)] shrink-0 size-[16px] border-[#d1d5db] bg-white border-[1.125px] cursor-pointer accent-[#121212]"
                          />
                        </div>
                        <label
                          htmlFor="accept-terms"
                          className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#3d3d3d] text-[14px] cursor-pointer"
                          style={{ fontFamily: FONT_SANS }}
                        >
                          <p className="leading-[20px] text-[#000000] text-[15px]">
                            {t('signup.terms_prefix')}{' '}
                            <a
                              href="https://biasafe.ai/terms-conditions"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline cursor-pointer hover:text-[#121212] font-medium"
                            >
                              {t('signup.terms_link')}
                            </a>{' '}
                            {t('signup.terms_and')}{' '}
                            <a
                              href="https://biasafe.ai/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline cursor-pointer hover:text-[#121212] font-medium"
                            >
                              {t('signup.privacy_link')}
                            </a>
                          </p>
                        </label>
                      </div>
                    </div>

                    <p id="accept-terms-hint" className="sr-only">
                      {t('signup.terms_hint')}
                    </p>

                    {/* Sign Up + divider + SSO. */}
                    <div className="content-stretch flex flex-col gap-[18px] items-center relative shrink-0 w-full mt-2">
                      <form.Subscribe
                        selector={(state) => [state.isSubmitting]}
                      >
                        {([isSubmitting]) => {
                          // Always clickable (terms aside) — validation runs on
                          // submit so autofilled values aren't blocked by a
                          // stale canSubmit. Terms stay a hard gate.
                          const disabled =
                            !acceptedTerms || isSubmitting || isLoading;
                          return (
                            <button
                              type="submit"
                              disabled={disabled}
                              aria-describedby={
                                !acceptedTerms ? 'accept-terms-hint' : undefined
                              }
                              className={`relative rounded-lg shrink-0 w-full transition-colors ${
                                disabled
                                  ? 'bg-[#d1d5db] cursor-not-allowed'
                                  : 'bg-[#121212] hover:bg-[#2a2a2a] cursor-pointer'
                              }`}
                            >
                              <div className="flex flex-row items-center justify-center size-full">
                                <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[16px] py-[8px] relative w-full">
                                  <div
                                    className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap text-white"
                                    style={{ fontFamily: FONT_SANS }}
                                  >
                                    <p className="leading-[24px] whitespace-pre">
                                      {isLoading
                                        ? t('signup.submitting')
                                        : t('signup.submit')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        }}
                      </form.Subscribe>

                      <Divider />

                      <div className="content-stretch flex gap-[12px] items-start relative shrink-0 w-full">
                        <SsoButton
                          onClick={beginGoogleSSO}
                          provider="google"
                          label={t('signup.google')}
                        />
                        {/* Gated on config, not on a code deploy. The provider
                            must be enabled on the Identity Platform project or
                            the popup dies with auth/operation-not-allowed —
                            which is why this button was deleted the first
                            time, when there was no Azure tenant at all. */}
                        {microsoftSsoEnabled() && (
                          <SsoButton
                            onClick={beginMicrosoftSSO}
                            provider="microsoft"
                            label={t('signup.microsoft')}
                          />
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Helper components below — kept inline so the file reads as one ------

function NameField({
  form,
  name,
  label,
  placeholder,
  autoComplete,
}: {
  form: any;
  name: 'firstName' | 'lastName';
  label: string;
  placeholder: string;
  autoComplete: string;
}) {
  const { t } = useTranslation('auth');
  return (
    <form.Field
      name={name}
      validators={{
        onBlur: z.string().min(1, t('signup.name_required')),
        onSubmit: z.string().min(1, t('signup.name_required')),
      }}
      listeners={{ onChange: clearErrorsOnChange }}
    >
      {(field: any) => {
        const hasError = shouldShowFieldError(
          field.state.meta,
          form.state.submissionAttempts,
        );
        const errorText = firstFieldError(field.state.meta);
        return (
          <div className="flex flex-col gap-[8px] items-start justify-center relative shrink-0 flex-1 min-w-0">
            <div
              className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#121212] text-[16px]"
              style={{ fontFamily: FONT_SANS }}
            >
              <label
                htmlFor={field.name}
                className="leading-none text-sm font-medium text-[#000000] cursor-pointer"
              >
                {label}
              </label>
            </div>
            <div
              className={`relative rounded-md shrink-0 w-full border shadow-xs ${
                hasError
                  ? 'border-[#ff4d4f] bg-transparent'
                  : 'border-[#121212]/20 bg-transparent focus-within:border-[#121212]'
              }`}
            >
              <div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
                <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[12px] py-[0px] relative w-full">
                  <input
                    type="text"
                    placeholder={placeholder}
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e: any) => field.handleChange(e.target.value)}
                    aria-invalid={hasError}
                    aria-describedby={
                      hasError ? `${field.name}-error` : undefined
                    }
                    autoComplete={autoComplete}
                    style={{ fontFamily: FONT_SANS }}
                    className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#121212] h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none w-full"
                  />
                </div>
              </div>
            </div>
            {hasError && errorText && (
              <FieldError id={`${field.name}-error`}>{errorText}</FieldError>
            )}
          </div>
        );
      }}
    </form.Field>
  );
}

function EmailField({ form }: { form: any }) {
  const { t } = useTranslation('auth');
  const emailSchema = z
    .string()
    .min(1, t('signup.email_required'))
    .email(t('signup.email_invalid'));

  return (
    <form.Field
      name="email"
      validators={{ onBlur: emailSchema, onSubmit: emailSchema }}
      listeners={{ onChange: clearErrorsOnChange }}
    >
      {(field: any) => {
        const hasError = shouldShowFieldError(
          field.state.meta,
          form.state.submissionAttempts,
        );
        const errorText = firstFieldError(field.state.meta);
        return (
          <div className="content-stretch flex flex-col gap-[8px] items-start justify-center relative shrink-0 w-full">
            <div
              className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#121212] text-[16px] text-center text-nowrap"
              style={{ fontFamily: FONT_SANS }}
            >
              <label
                htmlFor="signup-email"
                className="leading-none text-sm font-medium text-[#000000] cursor-pointer"
              >
                {t('signup.email_label')}
              </label>
            </div>
            <div
              className={`relative rounded-md shrink-0 w-full border shadow-xs ${
                hasError
                  ? 'border-[#ff4d4f] bg-transparent'
                  : 'border-[#121212]/20 bg-transparent focus-within:border-[#121212]'
              }`}
            >
              <div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
                <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[12px] py-[0px] relative w-full">
                  <input
                    id="signup-email"
                    type="email"
                    placeholder={t('signup.email_placeholder')}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e: any) => field.handleChange(e.target.value)}
                    aria-invalid={hasError}
                    aria-describedby={
                      hasError ? `${field.name}-error` : undefined
                    }
                    autoComplete="email"
                    style={{ fontFamily: FONT_SANS }}
                    className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#121212] h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none w-full"
                  />
                </div>
              </div>
            </div>
            {hasError && errorText && (
              <FieldError id={`${field.name}-error`}>{errorText}</FieldError>
            )}
          </div>
        );
      }}
    </form.Field>
  );
}

function PasswordField({
  form,
  label,
  placeholder,
  autoComplete,
  showPassword,
  onToggleShow,
  started,
  onChangeStarted,
  showRequirements,
}: {
  form: any;
  label: string;
  placeholder: string;
  autoComplete: string;
  showPassword: boolean;
  onToggleShow: () => void;
  started: boolean;
  onChangeStarted: (v: boolean) => void;
  showRequirements?: boolean;
}) {
  const { t } = useTranslation('auth');
  const passwordSchema = z
    .string()
    .min(10, t('signup.password_min'));

  const [isFocused, setIsFocused] = useState(false);

  return (
    <form.Field
      name="password"
      validators={{ onBlur: passwordSchema, onSubmit: passwordSchema }}
      listeners={{ onChange: clearErrorsOnChange }}
    >
      {(field: any) => {
        const value = field.state.value;
        // The border only. This field already owns its copy: `showStrengthError`
        // drives both the message and the requirements checklist, and it is
        // gated on `started`, so adding a second message here would say the
        // same thing twice.
        const hasError = shouldShowFieldError(
          field.state.meta,
          form.state.submissionAttempts,
        );
        const isEmpty = value.length === 0;
        const passwordValid = passwordMeetsPolicy(value);
        const showStrengthError = started && (!passwordValid || isEmpty);
        // Drives the always-visible requirements checklist: show it the
        // moment the user engages the field, not only after a failure.

        return (
          <div className="content-stretch flex flex-col gap-[8px] items-start justify-center relative shrink-0 w-full">
            <div
              className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#121212] text-[16px] text-center text-nowrap"
              style={{ fontFamily: FONT_SANS }}
            >
              <label
                htmlFor={field.name}
                className="leading-none text-sm font-medium text-[#000000] cursor-pointer"
              >
                {label}
              </label>
            </div>
            <div
              className={`relative rounded-md shrink-0 w-full border shadow-xs ${
                hasError || showStrengthError
                  ? 'border-[#ff4d4f] bg-transparent'
                  : 'border-[#121212]/20 bg-transparent focus-within:border-[#121212]'
              }`}
            >
              <div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
                <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[12px] py-[0px] relative w-full">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={placeholder}
                    id={field.name}
                    name={field.name}
                    value={value}
                    onFocus={() => setIsFocused(true)}
                    onBlur={(e: any) => {
                      setIsFocused(false);
                      field.handleBlur(e);
                    }}
                    onChange={(e: any) => {
                      field.handleChange(e.target.value);
                      if (!started && e.target.value.length > 0) {
                        onChangeStarted(true);
                      }
                    }}
                    autoComplete={autoComplete}
                    style={{ fontFamily: FONT_SANS }}
                    className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-[200px] not-italic relative shrink-0 text-[#121212] h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none"
                  />
                  {/* Reachable by keyboard and named. It was `tabIndex={-1}`
                      with no label, so a screen-reader or keyboard user could
                      not reveal what they had typed, which matters more now
                      that there is no second box to catch a typo. */}
                  <button
                    type="button"
                    onClick={onToggleShow}
                    aria-label={
                      showPassword
                        ? t('signup.password_hide')
                        : t('signup.password_show')
                    }
                    aria-pressed={showPassword}
                    className="relative shrink-0 size-[20px] cursor-pointer hover:opacity-70 transition-opacity"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-[#5B5B5B]" />
                    ) : (
                      <Eye className="w-5 h-5 text-[#5B5B5B]" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            {/* Password requirements — ALWAYS VISIBLE once the field is
                engaged, never hidden behind a hover.

                These rules previously lived inside a tooltip that only
                appeared when `started && !passwordValid`, i.e. the user had to
                first type a bad password AND then hover the red error text to
                discover what was expected. Reported as "no instructions for
                the password — the user doesn't know the requirements".

                Now the checklist renders as soon as the field is focused or
                has content, and reads as guidance (grey -> green as each rule
                is satisfied) rather than as an error. The red "does not meet
                requirements" line is kept, but only after the user has
                actually typed something invalid. */}
            {showRequirements && (isFocused || started) && (
              <div className="flex flex-col gap-[8px] w-full">
                {showStrengthError && (
                  <div className="flex items-center gap-[6px]">
                    <Info className="w-[14px] h-[14px] text-[#ff4d4f]" />
                    <p
                      className="text-[#ff4d4f] text-[13px] font-medium"
                      style={{ fontFamily: FONT_SANS }}
                    >
                      {isEmpty
                        ? t('signup.password_required')
                        : t('signup.password_not_met')}
                    </p>
                  </div>
                )}
                <PasswordChecklist value={value} fontFamily={FONT_SANS} />
              </div>
            )}
          </div>
        );
      }}
    </form.Field>
  );
}

function Divider() {
  const { t } = useTranslation('auth');
  return (
    <div className="content-stretch flex gap-[16px] items-center relative shrink-0 w-full">
      <div className="basis-0 grow h-0 min-h-px min-w-px relative shrink-0">
        <div className="absolute bottom-0 left-0 right-0 top-[-1px]">
          <svg
            className="block size-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 183 1"
          >
            <line
              stroke="#d1d5db"
              strokeWidth="0.5"
              x2="183"
              y1="0.5"
              y2="0.5"
            />
          </svg>
        </div>
      </div>
      <div
        className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#9ca3af] text-[20px] text-center text-nowrap"
        style={{ fontFamily: FONT_SANS }}
      >
        <p className="leading-[20px] whitespace-pre">{t('signup.or')}</p>
      </div>
      <div className="basis-0 grow h-0 min-h-px min-w-px relative shrink-0">
        <div className="absolute bottom-0 left-0 right-0 top-[-1px]">
          <svg
            className="block size-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 183 1"
          >
            <line
              stroke="#d1d5db"
              strokeWidth="0.5"
              x2="183"
              y1="0.5"
              y2="0.5"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}
